import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  SearchBar,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTheme } from '@mui/material/styles';
import {
  NotificationSearchRecipientResponseContentObject,
  usePostNotificationSearchRecipient,
} from '../../../../api-refactored/manage-notifications/usePostNotifcationSearchRecipient';

const useStyles = makeStyles()((theme) =>
({
  dialog: {
    overflow: 'hidden',
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  buttonContainer: {
    marginTop: theme.spacing(8),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  errorMessage: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
}));

const tableColumns = (
  state: TableState,
  t: TFunction<'translation'>,
  featureValue: string
): MUIDataTableColumnDef[] => [
    {
      name: 'indexNo',
      label: t('announcementsNotifications->>SendNotification.IndexNo'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].indexNo || '-'}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'nricFinPassport',
      label: t('announcementsNotifications->>SendNotification.NricFinPassportNo'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '25%' } }),
      },
    },
    {
      name: 'name',
      label: t('announcementsNotifications->>SendNotification.Name'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '25%' } }),
      },
    },
    {
      name: 'emailMobile',
      label:
        featureValue !== 'SMS'
          ? t('announcementsNotifications->>SendNotification.EmailAddress')
          : t('announcementsNotifications->>SendNotification.MobileNo'),
      options: {
        sort: true,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '25%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].emailMobile || '-'}</Typography>
          </Box>
        ),
      },
    },
  ];

interface AddRecipientModalProps {
  featureValue: string;
  modalState: boolean;
  onDialogClose: () => void;
  selectedCandidates: NotificationSearchRecipientResponseContentObject[];
  setSelectedCandidates: React.Dispatch<
    React.SetStateAction<NotificationSearchRecipientResponseContentObject[]>
  >;
}

const AddRecipientModal = ({
  featureValue,
  modalState,
  onDialogClose,
  selectedCandidates,
  setSelectedCandidates,
}: AddRecipientModalProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const { state, dispatch: tableDispatch, tOptions, remoteTotalRowsSelected } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'candidateName',
    order: 'asc',
    size: 5,
    page: 0,
    search: '',
  });

  const { mutate: mutatePostNotificationSearchRecipient } = usePostNotificationSearchRecipient({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const displayErrorMessage = () =>
    setErrorMessage(t('announcementsNotifications->>SendNotification.NoRecipientErrorMessage'));

  useEffect(() => {
    if (remoteTotalRowsSelected) {
      setErrorMessage('');
    }
  }, [remoteTotalRowsSelected]);

  const submit = () => {
    const newSelectedCandidates = Object.values(state.rowsSelected).concat(selectedCandidates);
    setSelectedCandidates(newSelectedCandidates);
    closeModal();
  };

  const closeModal = () => {
    tableDispatch({ type: 'CLEAR_SELECTION' });
    setErrorMessage('');
    onDialogClose();
  };

  useEffect(() => {
    mutatePostNotificationSearchRecipient({
      q: state.search || '',
      notificationType: featureValue,
      isEmailNotification: featureValue === 'EMAIL',
      excludedUserUuidList: selectedCandidates.map((c) => c.uuid),
      apiPathParam: {
        page: state.page || 0,
        pageSize: state.size || 5,
      },
    });
  }, [
    selectedCandidates,
    featureValue,
    mutatePostNotificationSearchRecipient,
    state.page,
    state.search,
    state.size,
  ]);

  return (
    <>
      <Dialog
        id="group-withdrawal-add-candidates-dialog"
        fullWidth
        open={modalState}
        maxWidth="md"
        PaperProps={{ className: classes.dialog }}
      >
        <DialogTitle>{t('announcementsNotifications->>SendNotification.AddRecipient')}</DialogTitle>
        <DialogContent>
          <Box className={classes.searchContainer}>
            <SearchBar
              placeholder={t('announcementsNotifications->>SendNotification.SearchPlaceholder')}
              name="searchInput"
              ariaLabel="searchInput"
              defaultValue={state.search}
              onSearch={(searchStr: string) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr.trim(),
                });
              }}
            />
          </Box>

          <Box>
            <MUIDataTable
              state={state}
              dispatch={tableDispatch}
              columns={tableColumns(state, t, featureValue)}
              data={state.data}
              emptyDataLabel={t('announcementsNotifications->>SendNotification.NoRecipientMessage')}
              options={{
                ...tOptions,
                pagination: true,
                sort: true,
                selectToolbarPlacement: 'none',
                selectableRows: 'multiple',
              }}
              rowsPerPageOptions={[5, 10, 20, 40, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            {errorMessage && (
              <Typography className={classes.errorMessage} variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Button
              id="button-cancel-add-candidate"
              type="button"
              variant="text"
              onClick={closeModal}
            >
              {t('announcementsNotifications->>SendNotification.Cancel')}
            </Button>
            {remoteTotalRowsSelected === 0 ? (
              <Button id="button-disable-add" type="submit" onClick={displayErrorMessage}>
                <Icon
                  className={classes.addIcon}
                  size="M"
                  name={IconName.add}
                  color={theme.color.icon.iconInverse}
                />
                {t('announcementsNotifications->>SendNotification.AddRecipient')}
              </Button>
            ) : (
              <Button id="button-enable-add" type="submit" onClick={submit}>
                <Icon
                  className={classes.addIcon}
                  size="M"
                  name={IconName.add}
                  color={theme.color.icon.iconInverse}
                />
                {t(`withdrawal:SelectCandidates.Add {{candidateCount}} Recipient(s)`, {
                  candidateCount: remoteTotalRowsSelected,
                })}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddRecipientModal;
